import { useState, type ComponentType } from "react";
import { Link, Outlet, useRouterState, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { BadgeCheck, Building2, CreditCard, FileUp, GitBranch, KeyRound, LayoutGrid, ListPlus, Lock, Mail, Palette, Percent, Scale, ShieldCheck, ShieldUser, ShoppingBag, UserCircle, Users, UsersRound, Webhook, Zap } from "lucide-react";
import { useImportKinds } from "@/shared/ui/ImportButton";
import { fold } from "@/shared/lib/fold";
import { useCan } from "@/shared/lib/session";
import { PageHeader } from "@/shared/ui/Page";
import { SearchBox } from "@/shared/ui/SearchBox";
import { useVisibleApps } from "./apps";
import { useAreaSettings, type AreaKey } from "./areas";

interface Item { to: string; label: string; hint?: string; icon: ComponentType<{ className?: string }>; show: boolean }

/** The settings pages the user may open, in groups, each with what it's for; an app's settings appear only while the app is on. */
export function useSettingsGroups(): { label: string; items: Item[] }[] {
  const { t } = useTranslation();
  const can = useCan();
  const apps = useVisibleApps();
  const imports = useImportKinds();
  return [
    { label: t("settingsGroups.company"), items: [
      { to: "/settings/company", label: t("settings.company"), hint: t("settingsHub.company"), icon: Building2, show: can("core.company.read") },
      { to: "/settings/mail", label: t("settings.mail"), hint: t("settingsHub.mail"), icon: Mail, show: can("core.company.read") },
      { to: "/settings/document-design", label: t("design.title"), hint: t("settingsHub.design"), icon: Palette, show: can("core.company.read") },
      { to: "/settings/brand", label: t("brand.title"), hint: t("settingsHub.brand"), icon: BadgeCheck, show: can("core.company.update") },
      { to: "/settings/opening-balances", label: t("opening.title"), hint: t("settingsHub.opening"), icon: Scale, show: can("core.money_account.update") || can("invoicing.invoice.post") || can("inventory.bill.post") },
      { to: "/settings/import", label: t("imp.hubTitle"), hint: t("imp.hubHint"), icon: FileUp, show: (imports.data?.length ?? 0) > 0 },
      { to: "/settings/branches", label: t("settings.branches"), hint: t("settingsHub.branches"), icon: GitBranch, show: can("core.branch.read") },
      { to: "/settings/taxes", label: t("settings.taxes"), hint: t("settingsHub.taxes"), icon: Percent, show: can("core.tax_code.read") },
      { to: "/settings/subscription", label: t("sub.title"), hint: t("settingsHub.subscription"), icon: CreditCard, show: can("core.billing.manage") },
      { to: "/settings/privacy", label: t("privacy.title"), hint: t("settingsHub.privacy"), icon: ShieldUser, show: can("core.privacy.manage") },
    ] },
    { label: t("settingsGroups.team"), items: [
      { to: "/settings/users", label: t("settings.users"), hint: t("settingsHub.users"), icon: Users, show: can("core.user.read") },
      { to: "/settings/roles", label: t("settings.roles"), hint: t("settingsHub.roles"), icon: ShieldCheck, show: can("core.role.read") },
      { to: "/settings/teams", label: t("settings.teams"), hint: t("settingsHub.teams"), icon: UsersRound, show: can("core.team.read") },
    ] },
    { label: t("settingsGroups.apps"), items: [
      { to: "/settings/apps", label: t("apps.choose"), hint: t("settingsHub.apps"), icon: LayoutGrid, show: can("core.apps.manage") },
      { to: "/settings/custom-fields", label: t("custom.settingsTitle"), hint: t("custom.hub"), icon: ListPlus, show: can("core.custom_field.read") },
      { to: "/settings/automations", label: t("auto.title"), hint: t("auto.hub"), icon: Zap, show: can("core.automation.read") },
      ...apps.flatMap((a) => (a.settings ? [{ to: a.settings.to, label: t(`apps.${a.key}.name`), hint: t(`apps.${a.key}.description`), icon: a.icon, show: a.settings.perm.some(can) }] : [])),
    ] },
    { label: t("settingsGroups.integrations"), items: [
      { to: "/settings/api-keys", label: t("keys.title"), hint: t("settingsHub.apiKeys"), icon: KeyRound, show: can("core.apikey.manage") },
      { to: "/settings/webhooks", label: t("hooks.title"), hint: t("settingsHub.webhooks"), icon: Webhook, show: can("core.webhook.manage") },
      { to: "/settings/shops", label: t("shops.title"), hint: t("settingsHub.shops"), icon: ShoppingBag, show: can("invoicing.shop.read") && apps.some((a) => a.key === "invoicing") },
    ] },
    { label: t("settingsGroups.account"), items: [
      { to: "/settings/profile", label: t("profile.title"), hint: t("settingsHub.profile"), icon: UserCircle, show: true },
      { to: "/settings/security", label: t("settings.security"), hint: t("settingsHub.security"), icon: Lock, show: true },
    ] },
  ].map((g) => ({ ...g, items: g.items.filter((i) => i.show) })).filter((g) => g.items.length > 0);
}

/**
 * Settings' first page: every settings page the user may open, as a card saying what it's
 * for, under its group and found by typing; for an area (?area=, the menu's link under an
 * area with several apps) only the settings of its apps.
 */
export function SettingsHome() {
  const { t } = useTranslation();
  const { area } = useSearch({ strict: false }) as { area?: AreaKey };
  const groups = useSettingsGroups();
  const areaPages = useAreaSettings(area);
  const [q, setQ] = useState("");
  const shown = area && areaPages.length > 0
    ? [{ label: "", items: groups.flatMap((g) => g.items).filter((i) => areaPages.some((p) => p.to === i.to)) }]
    : groups.map((g) => ({ ...g, items: g.items.filter((i) => !q || fold(`${i.label} ${i.hint ?? ""}`).includes(fold(q.trim()))) })).filter((g) => g.items.length > 0);
  return (
    <>
      {!(area && areaPages.length > 0) && <div className="mb-7"><SearchBox value={q} onChange={setQ} /></div>}
      {shown.length === 0 && <p className="py-8 text-ink-3">{t("common.noResults")}</p>}
      <div className="space-y-8">
        {shown.map((g) => (
          <section key={g.label || "area"} aria-label={g.label || undefined}>
            {g.label && <h2 className="mb-3 text-[13px] font-semibold text-ink-3">{g.label}</h2>}
            <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {g.items.map((i) => (
                <li key={i.to}>
                  <Link to={i.to} className="flex h-full items-start gap-3 rounded-2xl border border-line p-4 transition-colors hover:border-ink-3/40 hover:bg-paper/60">
                    <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-ledger-soft text-ledger"><i.icon className="size-[18px]" aria-hidden /></span>
                    <span className="min-w-0">
                      <span className="block font-semibold">{i.label}</span>
                      {i.hint && <span className="mt-0.5 block text-[13px] leading-snug text-ink-3">{i.hint}</span>}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </>
  );
}

/** A settings page under a link back to the settings home; the home itself has none. */
export function SettingsLayout() {
  const { t } = useTranslation();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const groups = useSettingsGroups();
  const { area } = useSearch({ strict: false }) as { area?: AreaKey };
  const current = groups.flatMap((g) => g.items).filter((i) => path === i.to || path.startsWith(i.to + "/")).sort((a, b) => b.to.length - a.to.length)[0];
  const home = !current;
  const title = current?.label ?? (area ? t("nav.areaSettings", { area: t(`areas.${area}`) }) : t("settings.title"));
  return (
    <>
      <PageHeader title={title} back={home && !area ? undefined : { to: "/settings", label: t("settings.title") }} />
      <div className="max-w-5xl"><Outlet /></div>
    </>
  );
}
